import { CenterSpinner } from '@/components/ats/loadders';
import { useShowToast } from '@/hooks';
import { getScorecardPayload } from '@/utils/schedule-interview-helpers';
import {
  useGetReviewActionDetailsQuery,
  useGetReviewDetailsQuery,
  useSendReviewMutation,
  useUpdateReviewMutation,
  useCreateAdhocReviewMutation,
} from '@/store/query/reviews';
import {
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Text,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import type { MutableRefObject } from 'react';
import { useRef } from 'react';
import { ReviewForm } from './ReviewForm';

interface ReviewActivityShape {
  id?: string | number;
  [key: string]: unknown;
}

interface ReviewApplicationShape {
  id?: string | number;
  job_stage_id?: string | number;
  ats_job_stages?: Array<{ id?: string | number; name?: string }>;
  [key: string]: unknown;
}

interface ReviewRecordShape {
  id?: string | number;
  [key: string]: unknown;
}

type FormikRef = {
  validateForm?: () => void | Promise<void>;
  isValid?: boolean;
  values?: Record<string, any>;
  errors?: { scoreCards?: any[] };
  submitForm?: () => void | Promise<void>;
};

interface SendReviewDrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  activity?: ReviewActivityShape;
  application?: ReviewApplicationShape;
  refetch?: () => void;
  review?: ReviewRecordShape;
  isAdhoc?: boolean;
}

export const SendReviewDrawer = ({
  isOpen,
  onClose = () => {},
  activity = {},
  application = {},
  refetch = () => {},
  review = {},
  isAdhoc = false,
}: SendReviewDrawerProps) => {
  const formRef = useRef<FormikRef | null>(null);
  const scoreCardRef = useRef<FormikRef | null>(null);
  const { data: actionData, isLoading: isActionDataLoading } =
    useGetReviewActionDetailsQuery(
      { ats_job_stage_action_id: activity?.id },
      { skip: !activity?.id || isAdhoc }
    );
  const { data: reviewData, isLoading: isReviewDataLoading } =
    useGetReviewDetailsQuery(
      { reviewId: review?.id },
      { skip: !review?.id || isAdhoc }
    );
  const data = activity?.id ? actionData : reviewData;
  const isLoading = isActionDataLoading || isReviewDataLoading;

  const [sendReview, { isLoading: isSending }] = useSendReviewMutation();
  const [updateReview, { isLoading: isUpdating }] = useUpdateReviewMutation();
  const toast = useShowToast();

  const [createAdhocReview, { isLoading: isCreatingAdhoc }] =
    useCreateAdhocReviewMutation();

  const validateAndGeneratePayload = async () => {
    const scoreForm = scoreCardRef.current;
    await scoreForm?.validateForm?.();
    if (!scoreForm?.isValid) {
      return getFormErrors();
    }

    const {
      scoreCards = [],
      feedback_description,
      rating_description,
      is_feedback_mandatory,
    } = scoreForm?.values ?? {};

    return {
      payload: {
        scorecard_feedback_description: feedback_description,
        scorecard_rating_description: rating_description,
        is_feedback_mandatory: is_feedback_mandatory ?? false,
        questions: getScorecardPayload(scoreCards),
      },
      error: null,
    };
  };

  const handleSubmitSuccess = (actionType) => {
    toast({
      title: `Review ${actionType} successfully`,
      status: 'success',
    });
    onClose();
    refetch();
  };

  const handleAdhocSubmit = async (values) => {
    try {
      const result = await validateAndGeneratePayload();
      if (!result || result.error) return;

      const { payload } = result;

      await createAdhocReview({
        payload: {
          ats_job_stage_id: application.job_stage_id,
          ats_application_id: application?.id,
          params: {
            name: values.label,
            participant_ids: values.participant_ids,
            scorecard_completion_threshold:
              values.scorecard_completion_threshold ?? null,
            ...payload,
          },
        },
      }).unwrap();

      handleSubmitSuccess('created');
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      toast({
        title: 'Failed to create review',
        status: 'error',
        description: err?.data?.message || err?.message,
      });
    }
  };

  const handleStandardSubmit = async (values) => {
    try {
      const result = await validateAndGeneratePayload();
      if (!result || result.error) return;

      const { payload } = result;

      const params: Record<string, unknown> = {
        name: values.label,
        participant_ids: values.participant_ids,
        scorecard_completion_threshold:
          values.scorecard_completion_threshold ?? null,
        ats_application_id: application?.id,
        ...payload,
      };

      if (activity?.id) {
        params.ats_job_stage_action_id = activity.id;
        await sendReview({ payload: params }).unwrap();
        handleSubmitSuccess('sent');
      } else {
        await updateReview({
          payload: params,
          reviewId: review?.id,
        }).unwrap();
        handleSubmitSuccess('updated');
      }
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      console.log('Standard submit error:', err?.data?.message || err?.message);
      toast({
        title: `${activity?.id ? 'Sending' : 'Updating'} review failed`,
        status: 'error',
      });
    }
  };

  const handleSubmit = isAdhoc ? handleAdhocSubmit : handleStandardSubmit;

  const focusOnErrorElement = (errorFieldIds) => {
    let fieldName = errorFieldIds?.[0] || '';
    if (Array.isArray(fieldName) && fieldName?.length > 0) {
      fieldName = fieldName[0];
    }
    const element = document.querySelector(`input[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const generateFieldName = (
    index: number,
    fieldName: string,
    optionIndex?: number
  ) => {
    if (fieldName === 'options' && optionIndex !== undefined) {
      return `scoreCards.${index}.options.${optionIndex}.optionText`;
    } else {
      return `scoreCards.${index}.${fieldName}`;
    }
  };

  const getFormErrors = () => {
    const scoreForm = scoreCardRef.current;
    const scoreCardsErrors = scoreForm?.errors?.scoreCards ?? [];
    const errorFieldIds = scoreCardsErrors
      .map((error, index) => {
        if (!error) {
          return null;
        }
        const correspondingValue = scoreForm?.values?.scoreCards?.[index] || {};
        if (correspondingValue) {
          if (error?.question) {
            return generateFieldName(index, 'question');
          } else if (error?.options) {
            if (Array.isArray(error?.options)) {
              const filteredOptions = error?.options?.filter(
                (option) => Object.keys(option)?.length > 0
              );
              return filteredOptions.map((option, optionIndex) => {
                return generateFieldName(index, 'options', optionIndex);
              });
            }
          }
        }
        return null;
      })
      .filter((id) => id);
    focusOnErrorElement(errorFieldIds);
    return null;
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay zIndex="1400" />
      <DrawerContent w="600px" maxW="600px" borderTopLeftRadius="xl">
        <Center
          w="48px"
          h="48px"
          left="-70px"
          top="32px"
          pos="absolute"
          bg="#fff"
          borderRadius="full"
          onClick={onClose}
          cursor="pointer"
        >
          <Icon as={XIcon} w="20px" h="20px" />
        </Center>
        <DrawerHeader
          p="22px 30px"
          borderBottom="1px solid"
          borderColor="border-color"
        >
          <Text color={'secondary-900'} size="md-medium">
            {isAdhoc || activity?.id ? 'Send review' : 'Update review'}
          </Text>
        </DrawerHeader>
        <DrawerBody px="34px" overflowY="auto" overflowX="hidden">
          {isLoading && !isAdhoc ? (
            <CenterSpinner />
          ) : (
            <ReviewForm
              formRef={formRef as MutableRefObject<any>}
              scoreCardRef={scoreCardRef as MutableRefObject<any>}
              activity={isAdhoc ? null : data}
              handleSubmit={handleSubmit}
              application={application}
              isAdhoc={isAdhoc}
            />
          )}
        </DrawerBody>
        <DrawerFooter borderTop="1px solid" borderColor="border-color">
          <Button
            variant="secondary"
            mr={3}
            onClick={onClose}
            data-test-id="secondary-cta-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void formRef.current?.submitForm?.();
              void scoreCardRef.current?.submitForm?.();
            }}
            isLoading={isSending || isUpdating || isCreatingAdhoc}
            data-test-id="primary-cta-button"
          >
            {isAdhoc || activity?.id ? 'Send' : 'Update'} review
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
